import Button from "@/common/components/Button"
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder"
import TextArea from "@/common/components/TextArea"
import TextField from "@/common/components/TextField"
import useCompany from "@/common/hooks/queries/useCompany"
import { formatCurrency } from "@/common/utils/formatters"
import { strategy as paymentModalityStrategies } from "@/features/feeTemplates/constants/paymentModality.constant"
import { UserRoundIcon } from "lucide-react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import { SubdomainSection } from "@/common/components/SubdomainSection"
import { SpecialEntries } from "@/features/privateCompanies/components/SpecialEntries"
import BlockUnblockCompany from "./components/BlockUnblockCompany";
import DisableEnableCompany from "./components/DisableEnableCompany";
import { CompanyTimezoneSection } from "@/features/privateCompanies/components/CompanyTimezone";
import LoadingSpinner from "@/common/components/LoadingSpinner"
import CompanyDatesInfo from "@/features/privateCompanies/components/CompanyDatesInfo"

const Show = () => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();

  const {
    data: company,
    isPending: companyIsPending,
    isFetching: companyIsFetching,
    error: companyError = {} as any,
    refetch: refetchCompany,
  } = useCompany({ id: companyId || '' });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyError?.status}
      refetchFunction={refetchCompany}
    >
      <div className="flex flex-col relative bg-white-100 rounded-md center p-4 gap-4 overflow-y-auto max-h-[calc(100vh-192px)] w-full">
        <LoadingSpinner isLoading={companyIsFetching} className="absolute top-4 self-center" />

        <BlockUnblockCompany company={company} />

        <DisableEnableCompany company={company} />

        {company?.company_lead_id && (
          <div className="flex w-full justify-between items-center bg-white-100 rounded-lg p-2">
            <p className="font-bold text-lg">Detalhes da empresa</p>
            <Link
              to={`/${company?.kind === 'public_company' ? 'public' : 'private'}-clients/${
                company?.company_lead_id
              }/edit`}
            >
              <Button operationKlassToCheckPermission={`Admin::Funnel::${company?.kind === 'public_company' ? 'Public::Leads' : 'PrivateCompanyLeads'}::Show`}>Visualizar</Button>
            </Link>
          </div>
        )}
        <SpecialEntries
          specialEntries={company?.fee_rule?.special_entries}
          companyType={'establishment'}
          disabled={companyIsPending}
          operationKlassToCheckPermission="Admin::Client::Companies::FeeRuleUpdate"
        />

        <CompanyDatesInfo company={company} companyIsPending={companyIsPending} />

        <SubdomainSection url={company?.domains?.[0]?.host} />

        <CompanyTimezoneSection company={company} isCompanyPending={companyIsPending} />

        <div className="flex items-center justify-between">
          <p className="text-[18px] font-bold">
            Responsáveis internos
          </p>

          <Button
            operationKlassToCheckPermission="Admin::Client::Companies::Update"
            onClick={() => navigate(`/companies/${companyId}/edit`)}
            disabled={companyIsPending}
            data-testid="edit_button"
          >
            Editar
          </Button>
        </div>

        <div className="flex items-start gap-x-4">
          <div className="flex flex-col gap-y-1">
            <p className="text-xs">Responsável interno</p>

            <div className="flex gap-2 items-center">
              <ImageWithPlaceholder
                url={company?.commercial_manager?.profile_picture?.url}
                alt={company?.commercial_manager?.full_name}
                className="size-8"
                iconClassName="size-6"
                placeholderIcon={UserRoundIcon}
              />
              <p className="font-bold text-sm">{company?.commercial_manager.full_name}</p>
            </div>
          </div>

          <div className="flex flex-col gap-y-1">
            <p className="text-xs">Responsável interno substituto</p>

            <div className="flex gap-2 items-center">
              <ImageWithPlaceholder
                url={company?.commercial_manager_substitute?.profile_picture?.url || ''}
                alt={
                  company?.commercial_manager_substitute?.full_name
                    ? company?.commercial_manager_substitute?.full_name
                    : ''
                }
                className="size-8"
                iconClassName="size-6"
                placeholderIcon={UserRoundIcon}
              />
              <p className="font-bold text-sm">
                {company?.commercial_manager_substitute?.full_name
                  ? company?.commercial_manager_substitute?.full_name
                  : '--'}
              </p>
            </div>
          </div>
        </div>

        <TextArea
          label="Observações"
          rows={5}
          value={company?.observations}
          data-testid="observations_input"
          disabled
        />

        <TextField
          label="Gestor Master Frota"
          value={company?.master_client_user?.full_name || 'Não informado'}
          disabled
        />
        <div className="flex justify-between items-center gap-2">
          <p className="text-xl">Resumo do contrato</p>
          <div className="flex items-center gap-2">
            <Button
              name="show_contract"
              variant="success"
              operationKlassToCheckPermission="Admin::Client::Contracts::Show"
              tooltipPlace="right"
              onClick={() => navigate(`/companies/${companyId}/contracts/${company?.current_contract?.id}`)}
              disabled={!company?.current_contract?.id}
            >
              Ver contrato
            </Button>
          </div>
        </div>
        <TextField
          label="Modalidade de pagamento"
          value={company?.current_contract?.payment_modality && paymentModalityStrategies[company?.current_contract?.payment_modality as keyof typeof paymentModalityStrategies]}
          disabled
        />
        <TextField
          label="Crédito garantido"
          value={company?.granted_credit ? formatCurrency(parseFloat(company.granted_credit)) : 'Não informado'}
          disabled
        />
        <TextField
          label="Serviços"
          value={company?.current_contract?.product_categories?.length ? company.current_contract.product_categories.map(category => category.display_name).join(', ') : 'Não informado'}
          disabled
        />
        <TextField
          label="Quantidade estimada de equipamentos"
          value={company?.current_contract?.expected_equipment_quantity ? `${company.current_contract.expected_equipment_quantity}` : 'Não informado'}
          disabled
        />
        <p className="text-sm">Atualmente {company?.equipments_count} cadastrados</p>
      </div>
    </ErrorMessageWrapper>
  )
}

export default Show
